import React, { useContext, useEffect, useState } from 'react';

import { Card } from 'react-bootstrap';
import { Coupon } from '../Coupon';
import {
	couponDurationLabel,
	couponEnds
} from '../../../../utils/couponDisplay';
import { CheckoutSummarySkeleton } from '../LoadingSkeleton';

import { PlanContext } from '../Context/PlanContext';
import { ContentContext } from '../Context/ContentContext';
import { CheckoutPageContext } from '../Context/CheckoutPageContext';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { toAmount } from '../../../../utils/toAmount';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { CouponContext } from '../Context/CouponContext';
import { PlanButton } from '../PlanButton';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { isUrl } from '../../../../utils/validators/url';
import { BillingType } from '../constants/billingType';
import { useInstallmentTax } from '../hooks/useInstallmentTax';
import { planChargeBreakdown } from '../../../../utils/planCashDiscount';

const CYCLE_LABEL: Record<number, string> = {
	1: 'por semana',
	2: 'por quinzena',
	3: 'por mês',
	4: 'por bimestre',
	5: 'por trimestre',
	6: 'por semestre',
	7: 'por ano'
};

export const Sidebar = () => {
	const [label, setLabel] = useState<React.ReactNode>(null);

	const { customization, finance } = useContext(ApplicationContext);
	const pageSettings = useContext(CheckoutPageContext);

	const [currentMinusBg, setCurrentMinusBg] = useState<string>('unset');
	const [currentPlusBg, setCurrentPlusBg] = useState<string>('unset');

	const [currentMinusColor, setCurrentMinusColor] = useState<string>(
		customization?.primaryColor || '#fff'
	);
	const [currentPlusColor, setCurrentPlusColor] = useState<string>(
		customization?.primaryColor || '#fff'
	);

	useEffect(() => {
		setCurrentPlusColor(customization?.primaryColor || '#fff');
		setCurrentMinusColor(customization?.primaryColor || '#fff');
	}, [customization]);

	const { orderBumps } = useContext(OrderBumpContext);
	const bumpsTotal = orderBumps.reduce(
		(sum, bump) => sum + toAmount(bump.content.pricing?.price),
		0
	);

	const { plan, plans, installmentCount } = useContext(PlanContext);

	const {
		content,
		isLoading,
		currentPrice,
		setCurrentPrice,
		quantity,
		setQuantity,
		billingType,
		checkout
	} = useContext(ContentContext);
	// Paga à vista = 1x no cartão ou Pix (o pay força 1x no Pix). É o que libera o desconto à
	// vista do plano.
	const paidInCash =
		billingType === BillingType.Pix || (installmentCount ?? 1) === 1;
	const { coupon } = useContext(CouponContext);

	const image = content?.images.find((image) => {
		return image.type === 1;
	});

	useEffect(() => {
		const installments = content?.pricing.pricingSchema?.installmentsSchema;
		if (!installments || installments.length === 0) return;

		const firstInstallment = installments.find(
			(schema) => schema.count === 1
		);
		const secondInstallment = installments.find(
			(schema) => schema.count > 1
		);

		setLabel(
			<>
				1x de{' '}
				{formatCurrency(
					(firstInstallment?.price || 0) - (coupon?.flatDiscount || 0)
				)}{' '}
				+ <br />
				{installments.length - 1}x de{' '}
				{formatCurrency(secondInstallment?.price || 0)}
			</>
		);
	}, [content, coupon]);

	const handleIncreaseQuantity = () => {
		setQuantity((prev) => prev + 1);
		setCurrentPrice(
			(prev) => toAmount(prev) + toAmount(content?.pricing.price)
		);
	};

	const handleDecreaseQuantity = () => {
		if (quantity === 1) return;

		setQuantity((prev) => prev - 1);
		setCurrentPrice(
			(prev) => toAmount(prev) - toAmount(content?.pricing.price)
		);
	};

	// Checkout de PLANO não tem `content`: a conta do efeito seguinte virava
	// 0 × quantidade − desconto, e o valor da compra (pixel, tela do Pix,
	// onboarding) ia NEGATIVO com cupom. Aqui é o preço do plano com o cupom e,
	// no Pix (sempre 1x no pay), com o desconto à vista — o que o pay cobra.
	//
	// Efeito PRÓPRIO, de propósito: no checkout avulso o efeito seguinte não pode
	// rodar a cada troca de forma de pagamento (o formulário aplica ali os
	// descontos de Pix/cartão do produto e seria sobrescrito). E o `currentPrice`
	// não acompanha a parcela do cartão: ele zera os eventos do GA4 e é a soma
	// exigida no pagamento com dois cartões — o resumo usa a conta própria.
	const isPix = billingType === BillingType.Pix;
	useEffect(() => {
		if (content || !plan) return;
		setCurrentPrice(
			planChargeBreakdown(
				{ ...plan, price: toAmount(plan.price) },
				coupon,
				isPix
			).total
		);
	}, [content, plan, coupon, isPix]);

	useEffect(() => {
		if (!content && plan) return;

		const discount = toAmount(coupon?.flatDiscount);
		const availableCoupons = (coupon?.amount || 0) - (coupon?.used || 0);

		const priceWithoutDiscount =
			toAmount(content?.pricing.price) * quantity;

		availableCoupons >= quantity
			? setCurrentPrice(priceWithoutDiscount - discount * quantity)
			: setCurrentPrice(
					priceWithoutDiscount - discount * availableCoupons
			  );
	}, [quantity, coupon, plan]);

	const [hasDiscount, setHasDiscount] = useState(false);
	useEffect(() => {
		if (coupon) return setHasDiscount(true);

		if (
			billingType === BillingType.CreditCard &&
			content?.pricing.flatCreditCardDiscount
		)
			return setHasDiscount(true);

		if (billingType === BillingType.Pix && content?.pricing.flatPixDiscount)
			return setHasDiscount(true);

		if (
			billingType === BillingType.Boleto &&
			content?.pricing.flatTicketDiscount
		)
			return setHasDiscount(true);

		return setHasDiscount(false);
	}, [coupon, billingType]);

	// Parcela em destaque (flag do checkout, aba Checkout do produto): o maior
	// parcelamento vira o valor grande e o total à vista fica menor, abaixo.
	// O parcelamento é sempre no cartão, então a base é o preço no cartão
	// (cupom e desconto do cartão), não o `currentPrice` da forma selecionada.
	// Mesmas regras de juros e parcelas ativas do seletor (`InstallmentsInput`),
	// para o valor exibido bater com o que o comprador vai pagar.
	const { calculate: calculateInstallments, installments } =
		useInstallmentTax();

	const maxInstallment = content?.pricing.maxInstallment || 1;
	const shouldHighlightInstallments =
		!!checkout?.highlightInstallments &&
		!!content?.pricing.isInstallmentEnabled &&
		!content?.pricing.isSmartInstallmentEnabled &&
		maxInstallment > 1;

	useEffect(() => {
		if (!shouldHighlightInstallments || !content) return;

		const couponDiscount = coupon?.flatDiscount || 0;
		const availableCoupons = (coupon?.amount || 0) - (coupon?.used || 0);
		const discountedUnits = coupon
			? Math.min(quantity, Math.max(availableCoupons, 0))
			: 0;

		const creditCardPrice = Math.max(
			content.pricing.price * quantity -
				couponDiscount * discountedUnits -
				(content.pricing.flatCreditCardDiscount || 0),
			0
		);

		const hasTaxes =
			(finance?.isAnticipationEnabled ?? true) &&
			// Campo vazio = a plataforma paga os juros (é o que o pay cobra).
			content.pricing.pricingSchema?.anticipationResponsible ===
				'CUSTOMER';

		calculateInstallments(
			creditCardPrice + bumpsTotal,
			maxInstallment,
			hasTaxes
		);
	}, [
		shouldHighlightInstallments,
		content,
		coupon,
		quantity,
		bumpsTotal,
		finance
	]);

	const highlightedInstallment = shouldHighlightInstallments
		? installments[installments.length - 1]
		: undefined;
	const isHighlightingInstallments =
		!!highlightedInstallment && highlightedInstallment.number > 1;

	return (
		<div className="p-6 bg-white rounded-xl sidebar-grid-item">
			<div className="h-100 d-flex flex-column px-5">
				<Card.Header className="pt-md-6 py-0 pb-4 d-flex align-items-center justify-content-between bg-transparent px-0 border-0">
					{/* `flex-grow-1` + `checkout-logo-align-*`: o alinhamento
					    escolhido no builder só funciona se o título ocupar a
					    linha inteira — como filho único de um header
					    `justify-content-between`, ele encolhia ao conteúdo. */}
					<Card.Title
						className={`mb-0 flex-grow-1 checkout-logo-align-${
							pageSettings.logoAlign ?? 'left'
						}`}
					>
						{/* Logo do checkout (builder) tem precedência sobre a
						    da plataforma. */}
						{pageSettings.logoUrl ? (
							<img
								src={pageSettings.logoUrl}
								alt=""
								className="checkout-logo"
							/>
						) : (
							customization && (
								<img
									src={
										isUrl(customization?.logo)
											? customization?.logo
											: `${customization?.url}${
													customization?.logo
											  }?d=${Date.now()}`
									}
									alt=""
									className="checkout-logo"
								/>
							)
						)}
					</Card.Title>
				</Card.Header>
				<Card.Body className="pt-4 pb-10 pb-md-5 px-0">
					{isLoading ? (
						<CheckoutSummarySkeleton />
					) : (
						<>
							{content && (
								<>
									{/* Resumo estilo Stripe: itens
									    discriminados + total. A imagem é
									    configurável (miniatura/grande/
									    oculta) no builder. */}
									{(pageSettings.summaryImage ?? 'thumb') ===
										'full' &&
										image && (
											<div
												style={{
													marginLeft: '-36px',
													marginRight: '-36px'
												}}
											>
												<img
													src={
														isUrl(
															`${image?.path}${image?.name}`
														)
															? `${image?.path}${image?.name}`
															: `${customization?.url}${image?.path}${image?.name}`
													}
													alt=""
													style={{
														maxHeight: '300px',
														objectFit: 'contain'
													}}
													className="w-100 mb-8"
												/>
											</div>
										)}

									<div className="summary-items">
										<div className="summary-item">
											{(pageSettings.summaryImage ??
												'thumb') === 'thumb' &&
												image && (
													<img
														className="summary-thumb"
														src={
															isUrl(
																`${image?.path}${image?.name}`
															)
																? `${image?.path}${image?.name}`
																: `${customization?.url}${image?.path}${image?.name}`
														}
														alt=""
													/>
												)}
											<div className="summary-item-info">
												<span className="summary-item-name">
													{content?.title ||
														plan?.name}
												</span>
												{content.pricing
													?.isBulkPurchaseEnabled && (
													<span className="summary-qty">
														<button
															type="button"
															onClick={
																handleDecreaseQuantity
															}
															className="summary-qty-btn"
														>
															−
														</button>
														<b>{quantity}</b>
														<button
															type="button"
															onClick={
																handleIncreaseQuantity
															}
															className="summary-qty-btn"
														>
															+
														</button>
													</span>
												)}
											</div>
											<span className="summary-item-price">
												{formatCurrency(
													(content?.pricing.price ||
														0) * quantity
												)}
											</span>
										</div>

										{orderBumps.map((bump) => (
											<div
												className="summary-item"
												key={bump.uuid}
											>
												<div className="summary-item-info">
													<span className="summary-item-name">
														{bump.content.title}
													</span>
													<small>
														Oferta adicional
													</small>
												</div>
												<span className="summary-item-price">
													{formatCurrency(
														bump.content.pricing
															?.price || 0
													)}
												</span>
											</div>
										))}

										{hasDiscount &&
											(() => {
												const listTotal =
													((content?.pricing.price ||
														0) +
														bumpsTotal) *
													quantity;
												const currentTotal =
													toAmount(currentPrice) +
													bumpsTotal;
												const discount =
													listTotal - currentTotal;
												if (discount <= 0) return null;
												return (
													<div className="summary-item summary-item--discount">
														<div className="summary-item-info">
															<span className="summary-item-name">
																Desconto
															</span>
														</div>
														<span className="summary-item-price">
															−
															{formatCurrency(
																discount
															)}
														</span>
													</div>
												);
											})()}
									</div>

									<div className="summary-total">
										<span>Total</span>
										<b>
											{isHighlightingInstallments &&
											highlightedInstallment
												? `${
														highlightedInstallment.number
												  }x de ${formatCurrency(
														highlightedInstallment.price
												  )}`
												: label
												? label
												: formatCurrency(
														toAmount(currentPrice) +
															bumpsTotal
												  )}
										</b>
									</div>

									{isHighlightingInstallments && (
										<div className="summary-installments">
											ou{' '}
											<b>
												{formatCurrency(
													toAmount(currentPrice) +
														bumpsTotal
												)}
											</b>{' '}
											à vista
										</div>
									)}

									{/* Parcelamento em destaque (Configurações
										do builder): "ou até 12x de R$ X". */}
									{pageSettings.showInstallments &&
										!label &&
										!isHighlightingInstallments &&
										(() => {
											const schema =
												content?.pricing?.pricingSchema;
											const maxInstallment =
												schema?.maxInstallment || 1;
											if (maxInstallment <= 1)
												return null;
											const total =
												toAmount(currentPrice) +
												bumpsTotal;
											if (total <= 0) return null;
											const base = total / maxInstallment;
											// Mesma taxa progressiva do
											// seletor de parcelas (2,99%/mês).
											let taxes = 0;
											for (
												let i = 1;
												i <= maxInstallment;
												i++
											) {
												taxes += base * i * 0.0299;
											}
											const perInstallment =
												base +
												Math.ceil(taxes * 100) /
													100 /
													maxInstallment;
											return (
												<div className="summary-installments">
													ou até{' '}
													<b>
														{maxInstallment}x de{' '}
														{formatCurrency(
															perInstallment
														)}
													</b>{' '}
													no cartão
												</div>
											);
										})()}

									{content && (
										<div className="d-flex justify-content-start mt-4">
											<Coupon />
										</div>
									)}
								</>
							)}
							{plans && (
								<div>
									<span className="h4 text-gray-800">
										Escolha o seu plano:
									</span>
									{plans.map((plan) => (
										<PlanButton key={plan.id} plan={plan} />
									))}
									{plan &&
										(() => {
											// Mesma conta do pay: desconto à vista (1x/Pix,
											// ciclo elegível) e depois o cupom sobre o resto.
											const planAmount = {
												...plan,
												price: toAmount(plan.price)
											};
											const charge = planChargeBreakdown(
												planAmount,
												coupon,
												paidInCash
											);
											const inCash = planChargeBreakdown(
												planAmount,
												coupon,
												true
											);
											const discount =
												charge.couponDiscount;
											const cycleLabel =
												CYCLE_LABEL[plan.cycle] ?? '';

											return (
												<>
													{charge.cashDiscount >
														0 && (
														<div className="d-flex justify-content-between mt-6 checkout-success">
															<span>
																Desconto à vista{' '}
																<small>
																	(pagamento
																	em 1x)
																</small>
															</span>
															<b>
																−
																{formatCurrency(
																	charge.cashDiscount
																)}
															</b>
														</div>
													)}
													{discount > 0 && (
														<div
															className={`d-flex justify-content-between ${
																charge.cashDiscount >
																0
																	? 'mt-2'
																	: 'mt-6'
															} checkout-success`}
														>
															<span>
																Cupom{' '}
																{coupon?.code}{' '}
																<small>
																	(
																	{couponDurationLabel(
																		coupon
																	)}
																	)
																</small>
															</span>
															<b>
																−
																{formatCurrency(
																	discount
																)}
															</b>
														</div>
													)}
													<div
														className={
															discount > 0 ||
															charge.cashDiscount >
																0
																? 'summary-total mt-2'
																: 'summary-total mt-6'
														}
													>
														<span>Total</span>
														<b>
															{formatCurrency(
																charge.total
															)}{' '}
															<small>
																{cycleLabel}
															</small>
														</b>
													</div>
													{/* Parcelado com desconto à vista disponível: avisar
													    quanto sai em 1x, em vez de deixar o comprador
													    descobrir pela diferença no seletor de parcelas. */}
													{charge.cashDiscount ===
														0 &&
														inCash.cashDiscount >
															0 && (
															<div className="text-muted text-right">
																<small>
																	Em 1x
																	{plan.isPixEnabled
																		? ' ou no Pix'
																		: ''}{' '}
																	sai por{' '}
																	<b>
																		{formatCurrency(
																			inCash.total
																		)}
																	</b>{' '}
																	(
																	{formatCurrency(
																		inCash.cashDiscount
																	)}{' '}
																	de desconto)
																</small>
															</div>
														)}
													{discount > 0 &&
														couponEnds(coupon) && (
															<div className="text-muted text-right">
																<small>
																	Depois,{' '}
																	{formatCurrency(
																		charge.base
																	)}{' '}
																	{cycleLabel}
																</small>
															</div>
														)}
													{/* Cupom em assinatura só existe no Waid Pay v2. */}
													{finance?.gateway ===
														'MALGA' && (
														<div className="d-flex justify-content-start mt-4">
															<Coupon />
														</div>
													)}
												</>
											);
										})()}
								</div>
							)}
						</>
					)}
				</Card.Body>
				<div className="d-none d-md-block justify-self-end mb-15 text-center text-muted">
					Powered by <span className="font-weight-bolder">Waid</span>
				</div>
			</div>
		</div>
	);
};
